import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PenyuluhDesaDataType } from './types'
import type { MutationPenyuluhDesaValues } from './validations'

const DESA_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['desa', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PenyuluhDesaDataType>>
> => ({
  queryKey: DESA_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PenyuluhDesaDataType>>
      >('/kelembagaan/posluhdes', {
        params: {
          page: params?.page,
          s: params?.q ? `${params.q}` : undefined,
          mode: 'and',
          per_page: params?.perPage || undefined,
          id_provinsi: params?.province?.value || undefined,
          id_kota: params?.city?.value || undefined,
          id_kecamatan: params?.district?.value || undefined,
          active: params?.active ?? undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PenyuluhDesaDataType>> => ({
  queryKey: DESA_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PenyuluhDesaDataType>
      >(`/kelembagaan/posluhdes/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhDesaDataType>,
  MutationPenyuluhDesaValues
> => ({
  mutationFn: async (data: MutationPenyuluhDesaValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PenyuluhDesaDataType>
      >('/kelembagaan/posluhdes', {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        // tahun_berdiri: data.tahun_berdiri.toString(),
        id_penyuluh: data.id_penyuluh.value,
        struktural: {
          ketua: {
            ...data.struktural.ketua,
            tahun_menjabat: data.struktural.ketua.tahun_menjabat.toString()
          },
          sekretaris: {
            ...data.struktural.sekretaris,
            tahun_menjabat:
              data.struktural.sekretaris.tahun_menjabat?.toString()
          },
          bendahara: {
            ...data.struktural.bendahara,
            tahun_menjabat: data.struktural.bendahara.tahun_menjabat?.toString()
          }
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhDesaDataType>,
  MutationPenyuluhDesaValues
> => ({
  mutationFn: async (data: MutationPenyuluhDesaValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PenyuluhDesaDataType>
      >(`/kelembagaan/posluhdes/${data.id_penyuluh_desa}`, {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        tahun_berdiri: data.tahun_berdiri.toString(),
        id_penyuluh: data.id_penyuluh.value,
        struktural: {
          ketua: {
            ...data.struktural.ketua,
            tahun_menjabat: data.struktural.ketua.tahun_menjabat.toString()
          },
          sekretaris: {
            ...data.struktural.sekretaris,
            tahun_menjabat:
              data.struktural.sekretaris.tahun_menjabat?.toString()
          },
          bendahara: {
            ...data.struktural.bendahara,
            tahun_menjabat: data.struktural.bendahara.tahun_menjabat?.toString()
          }
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhDesaDataType>,
  { id_penyuluh_desa: string }
> => ({
  mutationFn: async (data: { id_penyuluh_desa: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PenyuluhDesaDataType>
      >(`/kelembagaan/posluhdes/${data.id_penyuluh_desa}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (filterParams?: DtActionsAtom) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/posluhdes/export`, {
        params: {
          page: filterParams?.page,
          s: filterParams?.q ? `${filterParams.q}` : undefined,
          mode: 'and',
          per_page: filterParams?.perPage || undefined,
          id_provinsi: filterParams?.province?.value || undefined,
          id_kota: filterParams?.city?.value || undefined,
          id_kecamatan: filterParams?.district?.value || undefined,
          active: filterParams?.active ?? undefined
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const penyuluhDesa = {
  queryKey: [DESA_QUERY_KEY(undefined)[0]],
  getAll,
  create,
  getById,
  update,
  deleteData,
  exportData
}
